import {SupportPage} from './pages/Support';
import {PaidLobbyPage} from './pages/PaidLobby';
import {tx, ptx, usePageText} from './lib/page-text';
import {ConferencePage} from './components/program/Conference';
import {PublicReplayPage} from './components/program/PublicReplay';
import { useEffect } from 'react';
import { Navigate, Route, Routes } from './lib/localized-router';
import { useAuth } from './lib/auth-store';
import { Layout } from './components/Layout';
import { OverviewPage } from './pages/Overview';
import { StreamsPage } from './pages/Streams';
import { BlueprintsPage } from './pages/Blueprints';
import { StreamDetailPage } from './pages/StreamDetail';
import { ApiKeysPage } from './pages/ApiKeys';
import { WebhooksPage } from './pages/Webhooks';
import { WebhookTesterPage } from './pages/WebhookTester';
import { NetworkTestPage } from './pages/NetworkTest';
import { StudioPage } from './pages/Studio';
import { RecordingsPage } from './pages/Recordings';
import { EmbedPage } from './pages/Embed';
import { SettingsPage } from './pages/Settings';
import { AnalyticsPage } from './pages/Analytics';
import { SdkPage } from './pages/Stubs';
import { ScenariosPage } from './pages/Scenarios';
import { ProjectsPage } from './pages/Projects';
import { ProjectDetailPage } from './pages/ProjectDetail';
import { ChatConsolePage } from './pages/ChatConsole';
import { SessionsPage } from './pages/Sessions';
import { RestreamPage } from './pages/Restream';
import { PremieresPage } from './pages/Premieres';
import { ClipsPage } from './pages/Clips';
import { CommercePage } from './pages/Catalog';
import { PublicBoardPage } from './components/InteractiveBoard';
import { BoardsPage } from './pages/Boards';
import { AudienceToolsPage } from './pages/AudienceTools';

/** Kardolive has exactly one sign-in surface: kardocloud.com/live/login. This
 *  app used to carry a second one, which meant two differently-styled login
 *  screens and a forgotten-password link that only existed on one of them.
 *  Absolute by default because the portal is served from two places — /app/ on
 *  the apex and the root of live.kardocloud.com — and a relative path would
 *  resolve to a 404 from the second. */
export const SIGN_IN_URL = import.meta.env.VITE_SIGN_IN_URL ?? 'https://kardocloud.com/live/login/';
export const SIGN_UP_URL = import.meta.env.VITE_SIGN_UP_URL ?? 'https://kardocloud.com/live/signup';

/** Leaves the SPA for the shared auth pages. `remember` sends the current
 *  location along so sign-in can return the user where they were headed. */
function SendToAuth({ to = SIGN_IN_URL, remember = false }: { to?: string; remember?: boolean }) {
  usePageText();
  useEffect(() => {
    const here = (window.location.hostname === 'live.kardocloud.com' ? '/app' : '') + window.location.pathname + window.location.search + window.location.hash;
    const q = remember && !/\/(login|signup)\/?$/.test(window.location.pathname)
      ? `?next=${encodeURIComponent(here)}`
      : '';
    window.location.replace(to + q);
  }, [to, remember]);
  return <div style={{ padding: 40, color: '#94a3b8', fontSize: 14 }}>{tx("Taking you to sign in…")}</div>;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  usePageText();
  const token = useAuth((s) => s.token);
  if (!token) return <SendToAuth remember />;
  return <>{children}</>;
}

export default function App() {
  usePageText();
  return (
    <Routes>
      {import.meta.env.VITE_PAID_LOBBY_ENABLED === 'true' && <Route path="/paid/:key" element={<PaidLobbyPage />} />}
      <Route path="/replay/:key/:recordingId" element={<PublicReplayPage />} />
      <Route path="/board/:key" element={<PublicBoardPage />} />
      <Route path="/audience/:key" element={<AudienceToolsPage />} />
      <Route path="/conference/:key" element={<ConferencePage />} />
      {/* Kept as routes only so old links and bookmarks land somewhere sensible. */}
      <Route path="/login" element={<SendToAuth />} />
      <Route path="/signup" element={<SendToAuth to={SIGN_UP_URL} />} />
      <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index element={<OverviewPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="chat" element={<ChatConsolePage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="streams" element={<StreamsPage />} />
        <Route path="streams/new" element={<Navigate to="/streams?create=1" replace />} />
        <Route path="boards" element={<BoardsPage />} />
        <Route path="blueprints" element={<BlueprintsPage />} />
        <Route path="streams/:id" element={<StreamDetailPage />} />
        <Route path="recordings" element={<RecordingsPage />} />
        <Route path="restream" element={<RestreamPage />} />
        <Route path="premieres" element={<PremieresPage />} />
        <Route path="clips" element={<ClipsPage />} />
        <Route path="catalog" element={<Navigate to="/commerce/catalog" replace />} />
        <Route path="commerce" element={<Navigate to="/commerce/catalog" replace />} />
        <Route path="commerce/:section" element={<CommercePage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="webhooks" element={<WebhooksPage />} />
        <Route path="webhook-tester" element={<WebhookTesterPage />} />
        <Route path="network-test" element={<NetworkTestPage />} />
        <Route path="studio" element={<StudioPage />} />
        <Route path="embed" element={<EmbedPage />} />
        <Route path="solutions" element={<Navigate to="/streams?create=1" replace />} />
        <Route path="templates" element={<Navigate to="/streams?create=1" replace />} />
        <Route path="verticals" element={<Navigate to="/streams?create=1" replace />} />
        <Route path="scenarios" element={<ScenariosPage />} />
        <Route path="sdk" element={<SdkPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="support/:id" element={<SupportPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
